import type { C2CResponse, PriceHistoryPoint } from '@/services/wapi/types/c2c'

const MONTH_NAMES = [
    '',
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
]

/**
 * Structured price chart data for rendering 3 charts.
 */
export interface PriceChartData {
    daily: PriceHistoryPoint[] | null
    monthly: PriceHistoryPoint[] | null
    daysUntilDepart: PriceHistoryPoint[] | null
    xTickValuesDaily: (string | number)[]
    xTickValuesMonthly: number[]
    xTickValuesDepartDays: number[]
    interpolationValues: Record<string, string>
}

function formatPrice(value: number | undefined): string | null {
    if (value == null) {
        return null
    }
    return `$${Math.round(value)}`
}

function formatMonthName(month: number | undefined): string | null {
    if (month == null) {
        return null
    }
    return MONTH_NAMES[month] ?? null
}

function formatDate(dateStr: string | undefined): string | null {
    if (!dateStr) {
        return null
    }
    // Parse date parts manually to avoid timezone shift
    // (new Date("2023-12-12") is UTC midnight, which rolls back a day in US timezones)
    const [year, month, day] = dateStr.split('-').map(Number)
    if (!year || !month || !day) {
        return null
    }
    const date = new Date(year, month - 1, day)
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
}

/**
 * Replaces __variable__ placeholders in a template string with values.
 * Unknown placeholders are left as-is.
 */
export function interpolateContent(template: string, values: Record<string, string>): string {
    return template.replace(/__([a-zA-Z0-9_]+)__/g, (match, key: string) => {
        return values[key] ?? match
    })
}

/**
 * Extracts and transforms price history data from WAPI response into 3-chart format.
 * Returns null when no price data is available.
 */
export function selectPriceHistory(data: C2CResponse): PriceChartData | null {
    const raw = data.priceHistory
    if (!raw || typeof raw !== 'object') {
        return null
    }

    const { daily, monthly, daysUntilDepart, metrics } = raw

    const hasDaily = daily && daily.length > 0
    const hasMonthly = monthly && monthly.length > 0
    const hasDaysUntilDepart = daysUntilDepart && daysUntilDepart.length > 0

    if (!hasDaily && !hasMonthly && !hasDaysUntilDepart) {
        return null
    }

    // Compute xTickValues for each chart (matching ui-react mapPriceHistory)
    const xTickValuesDaily = hasDaily
        ? daily.reduce<(string | number)[]>((arr, { x }, index) => {
              if (index === 0 || index === Math.ceil(daily.length / 2)) {
                  return [...arr, x]
              }
              return arr
          }, [])
        : []

    const xTickValuesMonthly = hasMonthly ? monthly.map((point) => point.x as number) : []

    const xTickValuesDepartDays = hasDaysUntilDepart
        ? daysUntilDepart.reduce<number[]>((arr, { x }) => {
              if (typeof x === 'number' && x % 10 === 0) {
                  return [...arr, x]
              }
              return arr
          }, [])
        : []

    // Build interpolation values from metrics and stats
    const interpolationValues: Record<string, string> = {}

    if (metrics) {
        const entries: [string, string | null][] = [
            ['lowPriceMonthPrice', formatPrice(metrics.lowPriceMonthPrice)],
            ['highPriceMonthPrice', formatPrice(metrics.highPriceMonthPrice)],
            ['lowPriceMonth', formatMonthName(metrics.lowPriceMonth)],
            ['highPriceMonth', formatMonthName(metrics.highPriceMonth)],
            ['lowUpcomingWeekPrice', formatPrice(metrics.lowUpcomingWeekPrice)],
            ['highUpcomingWeekPrice', formatPrice(metrics.highUpcomingWeekPrice)],
            ['lowUpcomingMonthPrice', formatPrice(metrics.lowUpcomingMonthPrice)],
            ['highUpcomingMonthPrice', formatPrice(metrics.highUpcomingMonthPrice)],
            ['lowUpcomingMonthPriceDate', formatDate(metrics.lowUpcomingMonthPriceDate)],
            ['lowUpcomingWeekDate', formatDate(metrics.lowUpcomingWeekDate)],
            ['highUpcomingWeekDate', formatDate(metrics.highUpcomingWeekDate)],
            ['commonMonth', formatMonthName(metrics.commonMonth)],
            ['commonWeekPrice', formatPrice(metrics.commonWeekPrice)],
            ['commonMonthPrice', formatPrice(metrics.commonMonthPrice)],
        ]

        for (const [key, value] of entries) {
            if (value) {
                interpolationValues[key] = value
            }
        }
    }

    // From stats.metrics (cheapestAveragePriceUsd → __price__, savingsPriceUsd → __savingsPrice__)
    const statsMetrics = data.stats?.metrics
    if (statsMetrics?.cheapestAveragePriceUsd != null) {
        interpolationValues.price = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        }).format(statsMetrics.cheapestAveragePriceUsd)
    }
    if (statsMetrics?.savingsPriceUsd != null) {
        interpolationValues.savingsPrice = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        }).format(statsMetrics.savingsPriceUsd)
    }

    return {
        daily: hasDaily ? daily : null,
        monthly: hasMonthly ? monthly : null,
        daysUntilDepart: hasDaysUntilDepart ? daysUntilDepart : null,
        xTickValuesDaily,
        xTickValuesMonthly,
        xTickValuesDepartDays,
        interpolationValues,
    }
}
